import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "./client";

// Replacement for the old localStorage store — now backed by FastAPI + MongoDB.
// Provides a similar shape to App.js but operates on remote state and refetches after mutations.

const buildSingleTicket = (t) => ({
  status: t.status,
  buyer: t.buyer,
  phone: t.phone,
  email: t.email || "",
  address: t.address || "",
  seller: t.sold_by_name || "",
  date: t.date,
  assigned_seller_id: t.assigned_seller_id,
  assigned_seller_name: t.assigned_seller_name,
  assigned_seller_phone: t.assigned_seller_phone || "",
  has_prior_buyer: !!t.has_prior_buyer,
  prior_buyer_name: t.prior_buyer_name || "",
  prior_buyer_year: t.prior_buyer_year || null,
  prior_phone: t.prior_phone || "",
  called: !!t.called,
  called_by_name: t.called_by_name || "",
  called_at: t.called_at || "",
  called_note: t.called_note || "",
  tender_type: t.tender_type || "",
  tender_amount: t.tender_amount || 0,
  check_number: t.check_number || "",
  wins: t.wins || [],
  can_edit: t.can_edit,
  read_only: !!t.read_only,
});

const buildTicketsObj = (arr) => {
  const o = {};
  for (const t of arr) {
    o[t.number] = buildSingleTicket(t);
  }
  return o;
};

export const useAppData = (user) => {
  const [tickets, setTickets] = useState({});
  const [log, setLog] = useState([]);
  const [users, setUsers] = useState([]); // populated only for admin
  const [stats, setStats] = useState({ total: 400, sold: 0, reserved: 0, funds_received: 0, available: 400, pledged_revenue: 0, collected_revenue: 0 });
  const [settings, setSettings] = useState({ current_year: 2026, year_started_at: null, history_years: [] });
  const [historyOverview, setHistoryOverview] = useState({ returning_buyers: 0, tickets: [] });
  const [drawings, setDrawings] = useState([]);
  const [year, setYearState] = useState(2026);
  const [viewingYear, setViewingYear] = useState(null); // null = live current year
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    try {
      const [tRes, lRes, sRes, setRes, hRes] = await Promise.all([
        api.get("/tickets"),
        api.get("/log"),
        api.get("/stats"),
        api.get("/settings"),
        api.get("/history"),
      ]);
      setTickets(buildTicketsObj(tRes.data));
      setLog(lRes.data);
      setStats(sRes.data);
      setSettings(setRes.data);
      setHistoryOverview(hRes.data);
      if (setRes.data?.current_year) setYearState(setRes.data.current_year);
      if (user.role === "admin") {
        const uRes = await api.get("/users");
        setUsers(uRes.data);
        try {
          const dRes = await api.get("/drawings");
          setDrawings(dRes.data);
        } catch {
          setDrawings([]);
        }
      } else {
        try {
          const uRes = await api.get("/users");
          setUsers(uRes.data);
        } catch {
          setUsers([]);
        }
        setDrawings([]);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchYear = useCallback(async (targetYear) => {
    if (!user) return;
    try {
      const [tRes, sRes, lRes] = await Promise.all([
        api.get(`/tickets/by-year/${targetYear}`),
        api.get(`/stats/by-year/${targetYear}`),
        api.get(`/log/archive/${targetYear}`),
      ]);
      setTickets(buildTicketsObj(tRes.data));
      setStats(sRes.data);
      setLog(lRes.data);
      setHistoryOverview({ returning_buyers: 0, tickets: [] });
      if (user.role === "admin") {
        try {
          const dRes = await api.get(`/drawings/by-year/${targetYear}`);
          setDrawings(dRes.data);
        } catch {
          setDrawings([]);
        }
      } else {
        setDrawings([]);
      }
    } catch (e) {
      console.warn("fetchYear failed", e);
    }
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Multi-admin scenario: auto-refresh when the tab regains focus or becomes visible
  // so changes made by other admins (assigns, deletes, etc.) appear without a manual reload.
  useEffect(() => {
    if (!user) return;
    const onFocus = () => {
      if (viewingYear === null) fetchAll();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible" && viewingYear === null) fetchAll();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, fetchAll, viewingYear]);

  // Year switching: when admin picks a closed year from the dropdown, load read-only history.
  const setYear = useCallback((y) => {
    setYearState(y);
    if (settings?.current_year && y !== settings.current_year) {
      setViewingYear(y);
      fetchYear(y);
    } else {
      setViewingYear(null);
      fetchAll();
    }
  }, [settings?.current_year, fetchYear, fetchAll]);

  const applyOptimisticTicket = useCallback((t) => {
    if (!t || !t.number) return;
    setTickets((prev) => ({ ...prev, [t.number]: buildSingleTicket(t) }));
  }, []);

  const applyOptimisticTickets = useCallback((arr) => {
    if (!arr || arr.length === 0) return;
    setTickets((prev) => {
      const next = { ...prev };
      arr.forEach((t) => { if (t && t.number) next[t.number] = buildSingleTicket(t); });
      return next;
    });
  }, []);

  const recordTicket = useCallback(async ({ number, buyer, phone, email = "", address = "", action, assign_to_seller_id = null, tender = null }) => {
    try {
      const payload = { buyer, phone, action };
      if (email) payload.email = email;
      if (address) payload.address = address;
      if (assign_to_seller_id) payload.assign_to_seller_id = assign_to_seller_id;
      if (tender) {
        payload.tender_type = tender.tender_type;
        payload.tender_amount = tender.tender_amount;
        payload.check_number = tender.check_number || "";
      }
      const { data } = await api.post(`/tickets/${Number(number)}/record`, payload);
      if (data?.ticket) applyOptimisticTicket(data.ticket);
      await fetchAll();
      return {
        ok: true,
        status: action,
        assigned_seller_name: data?.assigned_seller_name || "",
        ticket: data?.ticket || null,
      };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not record." };
    }
  }, [fetchAll, applyOptimisticTicket]);

  const releaseTicket = useCallback(async (n) => {
    try {
      const { data } = await api.post(`/tickets/${Number(n)}/release`);
      if (data?.ticket) applyOptimisticTicket(data.ticket);
      await fetchAll();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not release." };
    }
  }, [fetchAll, applyOptimisticTicket]);

  const markSold = useCallback(async (n) => {
    try {
      const { data } = await api.post(`/tickets/${Number(n)}/mark-sold`);
      if (data?.ticket) applyOptimisticTicket(data.ticket);
      await fetchAll();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not mark sold." };
    }
  }, [fetchAll, applyOptimisticTicket]);

  const markFundsReceived = useCallback(async (n, tender) => {
    try {
      const { data } = await api.post(`/tickets/${Number(n)}/mark-funds-received`, tender || {});
      if (data?.ticket) applyOptimisticTicket(data.ticket);
      await fetchAll();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not mark funds received." };
    }
  }, [fetchAll, applyOptimisticTicket]);

  const bulkMarkFundsReceived = useCallback(async (numbers, tender) => {
    try {
      const { data } = await api.post("/tickets/bulk-mark-funds-received", { numbers, ...(tender || {}) });
      if (data?.tickets) applyOptimisticTickets(data.tickets);
      await fetchAll();
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not record funds." };
    }
  }, [fetchAll, applyOptimisticTickets]);

  const markCalled = useCallback(async (n, { called, note = "" } = {}) => {
    try {
      await api.post(`/tickets/${Number(n)}/mark-called`, { called: !!called, note });
      await fetchAll();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not update call status." };
    }
  }, [fetchAll]);

  const getTicketHistory = useCallback(async (n) => {
    try {
      const { data } = await api.get(`/tickets/${Number(n)}/history`);
      return { ok: true, history: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not load history.", history: [] };
    }
  }, []);

  const closeYear = useCallback(async (closingYear, newYear) => {
    try {
      const { data } = await api.post("/admin/close-year", { year: closingYear, new_year: newYear });
      await fetchAll();
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not close out the year." };
    }
  }, [fetchAll]);

  const startYear = useCallback(async () => {
    try {
      const { data } = await api.post("/admin/start-year");
      await fetchAll();
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not start the raffle year." };
    }
  }, [fetchAll]);

  const importHistory = useCallback(async ({ year: importYear, rows, replace = false }) => {
    try {
      const { data } = await api.post("/admin/import-history", { year: importYear, rows, replace });
      await fetchAll();
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not import history." };
    }
  }, [fetchAll]);

  const addUser = useCallback(async ({ email, password, name, phone, role }) => {
    try {
      const { data } = await api.post("/users", { email, password, name, phone: phone || "", role: role || "seller" });
      await fetchAll();
      return { ok: true, seller: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not add seller." };
    }
  }, [fetchAll]);

  const removeUser = useCallback(async (id) => {
    try {
      await api.delete(`/users/${id}`);
      await fetchAll();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not remove." };
    }
  }, [fetchAll]);

  const updateUser = useCallback(async (id, patch) => {
    try {
      await api.patch(`/users/${id}`, patch);
      await fetchAll();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not update." };
    }
  }, [fetchAll]);

  const resetUserPassword = useCallback(async (id, password) => {
    try {
      const body = password ? { password } : {};
      const { data } = await api.post(`/users/${id}/reset-password`, body);
      return {
        ok: true,
        generated: !!data.generated,
        temp_password: data.temp_password || "",
        user_name: data.user_name,
      };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not reset password." };
    }
  }, []);

  const assignTickets = useCallback(async (numbers, seller_id) => {
    try {
      await api.post("/tickets/assign", { numbers, seller_id });
      await fetchAll();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not assign." };
    }
  }, [fetchAll]);

  const unassignTickets = useCallback(async (numbers) => {
    try {
      await api.post("/tickets/unassign", { numbers });
      await fetchAll();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not unassign." };
    }
  }, [fetchAll]);

  // ---------- Drawings ----------
  const createDrawing = useCallback(async ({ name, drawing_date, drawing_type, prize_amount, notes = "" }) => {
    try {
      const { data } = await api.post("/drawings", { name, drawing_date, drawing_type, prize_amount, notes });
      setDrawings((prev) => [...prev, data].sort((a, b) => a.drawing_date.localeCompare(b.drawing_date)));
      return { ok: true, drawing: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not create drawing." };
    }
  }, []);

  const updateDrawing = useCallback(async (id, patch) => {
    try {
      const { data } = await api.patch(`/drawings/${id}`, patch);
      setDrawings((prev) => prev.map((d) => (d.id === id ? data : d)).sort((a, b) => a.drawing_date.localeCompare(b.drawing_date)));
      return { ok: true, drawing: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not update drawing." };
    }
  }, []);

  const deleteDrawing = useCallback(async (id) => {
    try {
      await api.delete(`/drawings/${id}`);
      setDrawings((prev) => prev.filter((d) => d.id !== id));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not delete drawing." };
    }
  }, []);

  const performDraw = useCallback(async (id, { mode, ticket_number = null, notes = "" }) => {
    try {
      const payload = { mode, notes };
      if (ticket_number != null) payload.ticket_number = Number(ticket_number);
      const { data } = await api.post(`/drawings/${id}/draw`, payload);
      setDrawings((prev) => prev.map((d) => (d.id === id ? data : d)));
      await fetchAll(); // refresh ticket wins map
      return { ok: true, drawing: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not perform drawing." };
    }
  }, [fetchAll]);

  const clearDrawing = useCallback(async (id) => {
    try {
      const { data } = await api.post(`/drawings/${id}/clear`);
      setDrawings((prev) => prev.map((d) => (d.id === id ? data : d)));
      await fetchAll();
      return { ok: true, drawing: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not clear winner." };
    }
  }, [fetchAll]);

  const fetchEligible = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/drawings/${id}/eligible`);
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not load eligible tickets.", count: 0, tickets: [] };
    }
  }, []);

  const updateBuyer = useCallback(async (number, patch) => {
    try {
      const { data } = await api.patch(`/tickets/${Number(number)}/buyer`, patch);
      if (data?.ticket) applyOptimisticTicket(data.ticket);
      return { ok: true, ticket: data?.ticket };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not update buyer." };
    }
  }, [applyOptimisticTicket]);

  const resendEmail = useCallback(async (number) => {
    try {
      const { data } = await api.post(`/tickets/${Number(number)}/resend-email`);
      // Backend now always returns 200; check the flag.
      if (data?.email_sent) {
        return { ok: true, message: data.message || "Email sent." };
      }
      return { ok: false, error: data?.message || "Could not send email." };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not send email." };
    }
  }, []);

  const syncBrevoContacts = useCallback(async () => {
    try {
      const { data } = await api.post(`/admin/sync-brevo-contacts`);
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not sync contacts." };
    }
  }, []);

  const getPaymentReminderEligible = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/payment-reminders/eligible`);
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not load recipients." };
    }
  }, []);

  const sendPaymentReminders = useCallback(async (drawingId) => {
    try {
      const { data } = await api.post(`/admin/payment-reminders`, { drawing_id: drawingId });
      return { ok: true, ...data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not send reminders." };
    }
  }, []);

  const setPrizePaid = useCallback(async (drawingId, paid) => {
    try {
      const { data } = await api.patch(`/drawings/${drawingId}/prize-paid`, { paid });
      setDrawings((prev) => prev.map((d) => (d.id === drawingId ? data : d)));
      return { ok: true, drawing: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e) || "Could not update prize status." };
    }
  }, []);

  return {
    tickets, log, users, stats, settings, historyOverview, drawings, year, setYear, loading,
    viewingYear, isReadOnlyYear: viewingYear !== null,
    recordTicket, releaseTicket, markSold, markFundsReceived, bulkMarkFundsReceived, markCalled,
    getTicketHistory, closeYear, startYear, importHistory,
    addUser, removeUser, updateUser, resetUserPassword, assignTickets, unassignTickets,
    createDrawing, updateDrawing, deleteDrawing, performDraw, clearDrawing, fetchEligible,
    setPrizePaid,
    updateBuyer, resendEmail, syncBrevoContacts,
    getPaymentReminderEligible, sendPaymentReminders,
    refresh: fetchAll,
  };
};
